import { useState } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '../../components/feature/Navbar';
import Footer from '../../components/feature/Footer';

export default function ForwardSystemPage() {
  const [expandedPillar, setExpandedPillar] = useState<number | null>(null);
  const [expandedFaq, setExpandedFaq] = useState<number | null>(null);

  const pillars = [
    {
      number: 1,
      title: 'Culture & Leadership Alignment',
      shortDesc: 'Safety starts at the top and flows through every level of leadership.',
      description: 'Build a unified leadership team that models safety behaviors, communicates expectations clearly, and creates psychological safety for frontline teams to speak up about risks.',
      components: [
        'Leadership safety vision workshops',
        'Executive coaching on safety communication',
        'Manager training on behavior-based observation',
        'Psychological safety assessment and improvement'
      ],
      outcomes: [
        'Leaders who walk the talk on safety',
        'Consistent messaging across all levels',
        'Employees feel safe reporting concerns',
        'Safety becomes part of business strategy'
      ],
      icon: 'ri-team-line'
    },
    {
      number: 2,
      title: 'Critical Behavior Focus',
      shortDesc: 'Identify and reinforce the daily behaviors that prevent incidents.',
      description: 'Move beyond generic safety rules to identify the specific behaviors that matter most in your operation. Train observers, track trends, and coach for improvement.',
      components: [
        'Critical behavior identification workshops',
        'Behavior observation card design',
        'Observer training and certification',
        'Positive reinforcement coaching techniques'
      ],
      outcomes: [
        'Clear focus on what matters most',
        'Frontline engagement in safety',
        'Behavior trends tracked over time',
        'Coaching replaces blame'
      ],
      icon: 'ri-eye-line'
    },
    {
      number: 3,
      title: 'Leading Indicator Scorecards',
      shortDesc: 'Measure what predicts incidents, not just what happened after.',
      description: 'Design custom scorecards that track proactive activities like observations, near-miss reports, training completion, and risk assessments — giving you early warning signs.',
      components: [
        'Custom scorecard design for your operation',
        'Data collection system setup',
        'Monthly scorecard review meetings',
        'Trend analysis and action planning'
      ],
      outcomes: [
        'Predict problems before they happen',
        'Data-driven safety decisions',
        'Accountability for proactive work',
        'Continuous improvement mindset'
      ],
      icon: 'ri-line-chart-line'
    },
    {
      number: 4,
      title: 'Risk Recognition & Frontline Empowerment',
      shortDesc: 'Train every employee to see hazards and feel empowered to act.',
      description: 'Equip frontline teams with practical risk assessment skills and the authority to stop work when needed. Build a culture where everyone is a safety leader.',
      components: [
        'Frontline risk recognition training',
        'Stop work authority protocols',
        'Near-miss reporting systems',
        'Hazard identification competitions'
      ],
      outcomes: [
        'Employees spot risks early',
        'Empowered to take action',
        'Near-miss reporting increases',
        'Ownership at all levels'
      ],
      icon: 'ri-shield-check-line'
    },
    {
      number: 5,
      title: 'Continuous Learning & Improvement',
      shortDesc: 'Turn every incident, near-miss, and observation into a learning opportunity.',
      description: 'Build systems that capture lessons learned, share best practices, and continuously refine your safety approach based on real data and frontline feedback.',
      components: [
        'Incident investigation training (root cause, not blame)',
        'Lessons learned communication systems',
        'Quarterly safety system reviews',
        'Best practice sharing across shifts/sites'
      ],
      outcomes: [
        'Learn from every event',
        'No repeat incidents',
        'Knowledge shared across teams',
        'Safety system evolves with your business'
      ],
      icon: 'ri-refresh-line'
    }
  ];

  const deliverables = [
    {
      icon: 'ri-presentation-line',
      title: 'Leadership Workshop',
      description: 'Full-day session to align your leadership team on safety vision and expectations'
    },
    {
      icon: 'ri-file-list-3-line',
      title: 'Behavior Observation Tools',
      description: 'Custom observation cards and tracking systems for your critical behaviors'
    },
    {
      icon: 'ri-dashboard-line',
      title: 'Scorecard Templates',
      description: 'Leading indicator dashboards tailored to your operation and goals'
    },
    {
      icon: 'ri-search-eye-line',
      title: 'Risk Assessment Framework',
      description: 'Practical tools for frontline teams to identify and control hazards'
    },
    {
      icon: 'ri-book-open-line',
      title: 'Training Materials',
      description: 'Ready-to-use training decks, videos, and job aids for your team'
    },
    {
      icon: 'ri-customer-service-2-line',
      title: 'Ongoing Support',
      description: 'Monthly coaching calls and quarterly reviews to keep momentum strong'
    }
  ];

  const faqs = [
    {
      question: 'Do you still help with OSHA compliance?',
      answer: 'Absolutely. Compliance is the foundation. We always start by making sure you\'re meeting OSHA requirements, have proper documentation, and are training your team correctly. The Forward Safety System™ is what we build on top of that solid compliance foundation.'
    },
    {
      question: 'Can this work for small businesses?',
      answer: 'Yes! The system scales to fit your size and resources. We tailor the approach based on your team size, complexity, and budget. Even small operations benefit from focusing on culture, behaviors, and leading indicators.'
    },
    {
      question: 'Do you work outside manufacturing?',
      answer: 'Definitely. While we have deep manufacturing experience, the Forward Safety System™ works across industries — warehousing, food production, construction, field services, facilities, and more. The principles of culture, behavior, and leading indicators apply everywhere.'
    },
    {
      question: 'How long does implementation take?',
      answer: 'Typically 3-6 months for initial implementation, depending on your starting point and scope. We work in phases so you see progress quickly. Most organizations start seeing cultural shifts and improved engagement within the first 60 days.'
    },
    {
      question: 'What if we already have a safety program?',
      answer: 'Perfect! We\'ll assess what\'s working, identify gaps, and layer in the Forward Safety System™ elements that will have the biggest impact. We build on your existing foundation rather than starting from scratch.'
    }
  ];

  return (
    <div className="min-h-screen bg-white">
      <Navbar />
      
      {/* Hero Section */}
      <section className="relative pt-32 pb-24 bg-[#14173A] overflow-hidden">
        <div className="absolute inset-0">
          <img 
            src="https://public.readdy.ai/ai/img_res/583a6e18026b8f610d6d1b14732a0ce8.jpg"
            alt="Team collaboration"
            className="w-full h-full object-cover object-top opacity-20"
          />
        </div>
        
        <div className="relative max-w-7xl mx-auto px-6 text-center">
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#D4AF37]/20 border border-[#D4AF37] mb-6">
            <span className="text-[#D4AF37] font-semibold text-sm uppercase tracking-wide">The Forward Safety System™</span>
          </div>
          
          <h1 className="text-6xl font-bold text-white mb-6 max-w-4xl mx-auto leading-tight">
            Five Pillars of Proactive Safety
          </h1>
          
          <p className="text-xl text-gray-300 max-w-3xl mx-auto mb-10 leading-relaxed">
            Move beyond compliance checklists to build a safety culture that prevents incidents through leadership, daily behaviors, and leading indicators.
          </p>
          
          <div className="flex items-center justify-center gap-3 text-gray-400 text-sm">
            <Link to="/" className="hover:text-[#D4AF37] transition-colors cursor-pointer">Home</Link>
            <i className="ri-arrow-right-s-line"></i>
            <span className="text-[#D4AF37]">Forward Safety System™</span>
          </div>
        </div>
      </section>

      {/* Five Pillars Visualization */}
      <section className="py-24 bg-[#F7F7F9]">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-5xl font-bold text-[#14173A] mb-4">The Five Pillars</h2>
            <p className="text-xl text-gray-600">A comprehensive framework for building proactive safety culture</p>
          </div>
          
          {/* Pillar Cards */}
          <div className="space-y-6 max-w-5xl mx-auto">
            {pillars.map((pillar) => (
              <div 
                key={pillar.number}
                className="bg-white rounded-3xl shadow-lg overflow-hidden transition-all duration-300 hover:shadow-xl"
              >
                <div 
                  className="p-8 cursor-pointer flex items-center justify-between"
                  onClick={() => setExpandedPillar(expandedPillar === pillar.number ? null : pillar.number)}
                >
                  <div className="flex items-center gap-6 flex-1">
                    <div className="w-16 h-16 flex items-center justify-center flex-shrink-0">
                      <i className={`${pillar.icon} text-[#D4AF37] text-5xl`}></i>
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center gap-4 mb-2">
                        <span className="text-3xl font-thin text-[#D4AF37]">0{pillar.number}</span>
                        <h3 className="text-2xl font-bold text-[#14173A]">{pillar.title}</h3>
                      </div>
                      <p className="text-gray-600">{pillar.shortDesc}</p>
                    </div>
                  </div>
                  <i className={`ri-arrow-${expandedPillar === pillar.number ? 'up' : 'down'}-s-line text-3xl text-[#D4AF37] transition-transform duration-300 flex-shrink-0`}></i>
                </div>
                
                {expandedPillar === pillar.number && (
                  <div className="px-8 pb-8 border-t border-gray-100">
                    <div className="pt-8">
                      <p className="text-lg text-gray-700 mb-8 leading-relaxed">{pillar.description}</p>
                      
                      <div className="grid md:grid-cols-2 gap-8">
                        <div>
                          <h4 className="text-lg font-bold text-[#D4AF37] mb-4 uppercase tracking-wide">Key Components</h4>
                          <ul className="space-y-3">
                            {pillar.components.map((component, i) => (
                              <li key={i} className="flex items-start gap-3">
                                <i className="ri-checkbox-circle-fill text-[#D4AF37] text-xl mt-0.5 flex-shrink-0"></i>
                                <span className="text-gray-700">{component}</span>
                              </li>
                            ))}
                          </ul>
                        </div>
                        
                        <div>
                          <h4 className="text-lg font-bold text-[#D4AF37] mb-4 uppercase tracking-wide">Outcomes</h4>
                          <ul className="space-y-3">
                            {pillar.outcomes.map((outcome, i) => (
                              <li key={i} className="flex items-start gap-3">
                                <i className="ri-star-fill text-[#D4AF37] text-xl mt-0.5 flex-shrink-0"></i>
                                <span className="text-gray-700">{outcome}</span>
                              </li>
                            ))}
                          </ul>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* What You Get */}
      <section className="py-24 bg-[#14173A]">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-5xl font-bold text-white mb-4">What You Get</h2>
            <p className="text-xl text-gray-300">Comprehensive deliverables to implement and sustain the system</p>
          </div>
          
          <div className="grid md:grid-cols-3 gap-8">
            {deliverables.map((item, index) => (
              <div 
                key={index}
                className="bg-[#1A1D3F] rounded-3xl p-8 hover:bg-[#22254A] transition-all duration-300 text-center"
              >
                <div className="w-16 h-16 flex items-center justify-center mx-auto mb-6">
                  <i className={`${item.icon} text-[#D4AF37] text-5xl`}></i>
                </div>
                <h3 className="text-xl font-bold text-white mb-3">{item.title}</h3>
                <p className="text-gray-300 leading-relaxed">{item.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-24 bg-[#F7F7F9]">
        <div className="max-w-5xl mx-auto px-6">
          <div className="grid md:grid-cols-5 gap-12">
            <div className="md:col-span-2">
              <h2 className="text-4xl font-bold text-[#14173A] mb-4 leading-tight">
                Frequently Asked Questions
              </h2>
              <div className="w-20 h-0.5 bg-[#D4AF37] mb-6"></div>
              <p className="text-gray-600 leading-relaxed">
                Common questions about implementing the Forward Safety System™ in your organization.
              </p>
            </div>
            
            <div className="md:col-span-3 space-y-4">
              {faqs.map((faq, index) => (
                <div 
                  key={index}
                  className="bg-white rounded-2xl shadow-md overflow-hidden transition-all duration-300"
                >
                  <div 
                    className="p-6 cursor-pointer flex items-start justify-between gap-4"
                    onClick={() => setExpandedFaq(expandedFaq === index ? null : index)}
                  >
                    <h3 className="text-lg font-bold text-[#14173A] flex-1">{faq.question}</h3>
                    <i className={`ri-${expandedFaq === index ? 'subtract' : 'add'}-line text-2xl text-[#D4AF37] flex-shrink-0 transition-transform duration-300`}></i>
                  </div>
                  
                  {expandedFaq === index && (
                    <div className="px-6 pb-6 border-t border-gray-100">
                      <p className="text-gray-600 leading-relaxed pt-4">{faq.answer}</p>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 bg-gradient-to-r from-[#D4AF37] to-[#C9A832]">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <h2 className="text-4xl font-bold text-[#14173A] mb-6">
            Ready to Implement the Forward Safety System™?
          </h2>
          <p className="text-xl text-[#14173A]/80 mb-10">
            Let's discuss how these five pillars can transform your safety culture.
          </p>
          <Link 
            to="/contact"
            className="inline-flex items-center gap-3 px-10 py-5 bg-white text-[#14173A] rounded-2xl font-bold text-lg hover:scale-105 transition-all duration-300 shadow-xl whitespace-nowrap cursor-pointer"
          >
            Request a Consultation
            <i className="ri-arrow-right-line text-xl"></i>
          </Link>
        </div>
      </section>

      <Footer />
    </div>
  );
}
